/**
 * アクセシビリティ用のフォーカス枠線を制御
 * focus-visible状態の要素に枠線を追従させる
 * 
 * @example
 * // 初期化
 * const focusManager = new A11yFocusOutline();
 * 
 * @example
 * // HTML側でスタイルを指定
 * <button data-a11y-selection-class="button-style">ボタン</button>
 */
class A11yFocusOutline {
    constructor() {
        this.outline = document.querySelector('.a11y-selection');
        this.targetElement = null;
        this.animationId = null;

        if (!this.outline) {
            console.error('.a11y-selection要素が見つからないよ！');
            return;
        }

        this.init();
    }

    /**
     * イベントリスナーを設定
     */
    init() {
        // focus-visibleをサポートしてるか確認
        document.addEventListener('focusin', (e) => {
            // focus-visibleの疑似クラスをチェック
            // ただし <main>（スキップリンクの飛び先・tabindex=-1）は巨大枠になるので除外
            if (e.target.matches(':focus-visible') && !e.target.matches('main, [data-a11y-no-outline]')) {
                this.startTracking(e.target);
            }
        });

        document.addEventListener('focusout', () => {
            this.stopTracking();
        });

        // Escキーでも非表示
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.stopTracking();
            }
        });
    }

    /**
     * 要素の追跡を開始
     * @param {HTMLElement} element - 追跡対象の要素
     */
    startTracking(element) {
        this.targetElement = element;

        // data-a11y-selection-classがあればdata属性を追加
        const customValue = element.dataset.a11ySelectionClass;
        if (customValue) {
            this.outline.dataset.a11ySelectionClass = customValue;
        }

        this.outline.style.opacity = '1';
        this.updatePosition();
    }

    /**
     * 要素の追跡を停止
     */
    stopTracking() {
        this.targetElement = null;
        this.outline.style.opacity = '0';

        // data属性を削除
        delete this.outline.dataset.a11ySelectionClass;

        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    /**
     * 枠線の位置とサイズを更新
     */
    updatePosition() {
        if (!this.targetElement) return;

        const rect = this.targetElement.getBoundingClientRect();

        this.outline.style.width = `${rect.width}px`;
        this.outline.style.height = `${rect.height}px`;
        this.outline.style.transform = `translate(${rect.left}px, ${rect.top}px)`;

        // 次のフレームでも更新
        this.animationId = requestAnimationFrame(() => this.updatePosition());
    }
}

/**
 * 本文スキップリンク：Enter/クリックで現在の <main> へ focus を移す。
 * href="#main" のネイティブ遷移は Lenis のアンカー委譲がスクロールだけ奪って focus を移さないため明示制御する。
 * main は永続ヘッダー外＝swupで差し替わるので、クリック時点で現在のmainを引き直す。
 */
const initSkipLink = () => {
    const link = document.querySelector('.skipLink');
    if (!link) return;
    link.addEventListener('click', (e) => {
        e.preventDefault();
        const main = document.querySelector('main:not(.is-previous-container)') || document.querySelector('main');
        if (!main) return;
        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
    });
};

// 初期化
document.addEventListener('DOMContentLoaded', () => {
    new A11yFocusOutline();
    initSkipLink();
});